import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { CellWrapper, CellWithoutDataContent, EllipsisText } from '../styles';
import { sanitizeNumericValueForPercentAndCurrency } from '../../../../utils';
import Input from '../StyledMaskedInput';

function CurrencyInput(props) {
  const {
    value: propValue,
    editable: propEditable,
    onChange,
    isMissing,
    isActive = false,
    isEditing = false,
    onStartEditing,
    onStopEditing,
    rowIndex,
    colIndex,
    registerCellRef,
  } = props;
  const normalizeValue = (v) => (v === 0 || v === '0' ? '' : v);
  const [value, setValue] = useState(normalizeValue(propValue));
  const [initialValue, setInitialValue] = useState(normalizeValue(propValue) || '');
  const [editable, setEditable] = useState(!!propEditable || isEditing);
  const inputRef = useRef(null);
  const cellRef = useRef(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (registerCellRef && typeof rowIndex === 'number' && typeof colIndex === 'number') {
      registerCellRef(rowIndex, colIndex, cellRef.current);

      return () => registerCellRef(rowIndex, colIndex, null);
    }

    return undefined;
  }, [registerCellRef, rowIndex, colIndex]);

  const isCurrentlyEditing = isEditing !== undefined ? isEditing : editable;

  useEffect(() => {
    const normalized = normalizeValue(propValue);
    if (normalized !== value && !propEditable && isEditing === undefined) {
      setValue(normalized);
      setInitialValue(normalized);
      setEditable(false);
    } else if (normalized !== value && isEditing === undefined) {
      setValue(normalized);
      setInitialValue(normalized);
    }
  }, [propValue, isEditing, value]);

  useEffect(() => {
    if (isEditing !== undefined) {
      setEditable(isEditing);
      if (isEditing && inputRef.current) {
        setTimeout(() => {
          const inputElement = inputRef.current;
          if (inputElement && inputElement.focus && typeof inputElement.focus === 'function') {
            inputElement.focus();
            if (inputElement.select && typeof inputElement.select === 'function') {
              inputElement.select();
            }
          }
        }, 0);
      }
    }
  }, [isEditing]);

  const handleChange = (e) => {
    let val = sanitizeNumericValueForPercentAndCurrency(e.target.value);
    setValue(val);
  };

  const handleBlur = (e) => {
    if (isSavingRef.current) {
      return;
    }

    let val = sanitizeNumericValueForPercentAndCurrency(e.target.value);

    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }

    if (onChange && val !== initialValue) {
      onChange(val);
    }
  };

  const handleEnterSave = () => {
    isSavingRef.current = true;

    let val = sanitizeNumericValueForPercentAndCurrency(value);

    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }

    if (onChange) {
      onChange(val);
    }

    setTimeout(() => {
      isSavingRef.current = false;
    }, 300);
  };

  const handleEscapeCancel = () => {
    isSavingRef.current = false;
    setValue(initialValue);
    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }
  };

  const handleEnterEdit = () => {
    if (!isCurrentlyEditing) {
      if (isEditing !== undefined && onStartEditing) {
        onStartEditing();
      } else {
        setEditable(true);
      }
    }
  };

  const edit = () => {
    if (isEditing !== undefined && onStartEditing) {
      onStartEditing();
    } else {
      setEditable(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isCurrentlyEditing) {
        handleEnterSave();
      } else {
        handleEnterEdit();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEscapeCancel();
    }
  };

  return (
    <CellWrapper
      ref={cellRef}
      isActive={isActive}
      isEditing={isCurrentlyEditing}
      onKeyDown={!isCurrentlyEditing ? handleKeyDown : undefined}
      tabIndex={!isCurrentlyEditing ? 0 : -1}
    >
      {isCurrentlyEditing ? (
        <Input
          ref={inputRef}
          value={value}
          onChange={handleChange}
          rate={26}
          type="text"
          autoFocus
          onPressEnter={handleEnterSave}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          typeOfMask="decimalCurrency"
        />
      ) : (
        <>
          {value ? (
            <EllipsisText
              onClick={edit}
              isActive={isActive}
            >
              {parseFloat(value).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}
            </EllipsisText>
          ) : (
            <CellWithoutDataContent
              onClick={edit}
              isMissing={isMissing}
              isActive={isActive}
            />
          )}
        </>
      )}
    </CellWrapper>
  );
}

CurrencyInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editable: PropTypes.bool,
  onChange: PropTypes.func,
  isMissing: PropTypes.bool,
  isActive: PropTypes.bool,
  isEditing: PropTypes.bool,
  onStartEditing: PropTypes.func,
  onStopEditing: PropTypes.func,
  rowIndex: PropTypes.number,
  colIndex: PropTypes.number,
  registerCellRef: PropTypes.func,
};

CurrencyInput.defaultProps = {
  value: '',
  onChange: undefined,
  editable: false,
  isMissing: false,
  isActive: false,
  isEditing: undefined,
  onStartEditing: null,
  onStopEditing: null,
  rowIndex: undefined,
  colIndex: undefined,
  registerCellRef: null,
};

export default CurrencyInput;
